import React, {FC, useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import {AnyRouteResult} from '@/app/interfaces/routing';
import {RouteName} from '@/app/constants/routing';
import {useRoute} from '@/common/services/router';
import {useLocalizer} from '@/common/services/localizer';
import {selectDomainConfig, selectLanguage} from '@/app/store/config/selectors';

/**
 * The component observes a language change and modify the language key in the url and apply a new localization of texts
 */
export const LanguageObserver: FC = props => {
	const {children} = props;

	const {route, history, urlGenerator} = useRoute<AnyRouteResult>();
	const localizer = useLocalizer();
	const profileLanguage = useSelector(selectLanguage);
	const {primaryLanguage} = useSelector(selectDomainConfig);

	// Update a language key in the url (if it is defined) on the profile language change
	const prevProfileLanguage = useRef(profileLanguage);
	useEffect(() => {
		if (prevProfileLanguage.current === profileLanguage) {
			return;
		}
		prevProfileLanguage.current = profileLanguage;

		const newRouteName = route.name === RouteName.notFound && route.originalName ? route.originalName : route.name;
		const newLocation = urlGenerator(newRouteName, {
			...route.params,
			lang: profileLanguage === primaryLanguage ? undefined : profileLanguage,
		} as any);

		const newPathname = decodeURIComponent(history!.createHref(newLocation));
		history!.replace(newPathname);

		document.documentElement.lang = profileLanguage || primaryLanguage;
	}, [profileLanguage]);

	// Update the page strings localization on language change
	useEffect(() => {
		if (profileLanguage) {
			localizer.setLanguage(profileLanguage);
		}
	}, [profileLanguage]);

	return <>{children}</>;
};

LanguageObserver.displayName = 'LanguageObserver';
